// EinwaageDashboard.jsx — Einwaage / Offizielles Wiegen
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './EinwaageDashboard.css';

const STATUS_LABEL = {
  ausstehend:       { label: 'Ausstehend',      css: 'ew-status-pending'  },
  bestanden:        { label: 'Bestanden ✓',      css: 'ew-status-pass'    },
  nicht_bestanden:  { label: 'Nicht bestanden',  css: 'ew-status-fail'    },
  nicht_erschienen: { label: 'Nicht erschienen', css: 'ew-status-absent'  },
};

function diffKg(angemeldet, einwaage) {
  if (angemeldet == null || einwaage == null || einwaage === '') return null;
  return (parseFloat(einwaage) - parseFloat(angemeldet)).toFixed(2);
}

// Extrahiert eine Zahl aus der Waagen-Ausgabe (z.B. "  65.50 kg" → "65.50")
function parseWaagenInput(raw) {
  if (!raw && raw !== 0) return '';
  const match = String(raw).replace(',', '.').match(/[\d]+\.?[\d]*/);
  return match ? match[0] : '';
}

// Findet die bessere Kategorie wenn Gewicht nicht in die angemeldete Klasse passt
function findBetterCategory(measuredWeight, athlete, weightClasses) {
  if (!measuredWeight || !athlete || !weightClasses?.length) return null;
  const measured = parseFloat(measuredWeight);
  if (isNaN(measured)) return null;

  const min = athlete.div_min != null && athlete.div_min !== '' ? parseFloat(athlete.div_min) : null;
  const max = athlete.div_max != null && athlete.div_max !== '' ? parseFloat(athlete.div_max) : null;

  const inRange = (min === null || measured >= min - 0.1) && (max === null || measured <= max + 0.05);
  if (inRange) return null;

  // Startet der Kämpfer in mehreren Divisionen, ist nicht entscheidbar, welche
  // gewechselt werden soll — der Wechsel würde eine beliebige umhängen. Dann
  // lieber keinen Vorschlag und die Entscheidung dem Personal überlassen.
  if ((athlete.anzahl_divisionen ?? 1) > 1) return null;

  const divType   = athlete.division_type?.toLowerCase();
  const divGender = athlete.div_gender?.toLowerCase();

  const match = weightClasses.find(wc => {
    if (wc.division_code === athlete.division_code) return false;
    if (wc.division_type?.toLowerCase() !== divType) return false;
    const wcGender = wc.gender?.toLowerCase();
    if (wcGender !== 'male/female' && divGender && wcGender && wcGender !== divGender) return false;
    const wcMin = wc.min_gewicht != null && wc.min_gewicht !== '' ? parseFloat(wc.min_gewicht) : 0;
    const wcMax = wc.max_gewicht != null && wc.max_gewicht !== '' ? parseFloat(wc.max_gewicht) : 999;
    return measured >= wcMin - 0.1 && measured <= wcMax + 0.05;
  });

  return match ?? null;
}

/**
 * Passt ein bereits gespeichertes Gewicht noch in die Klasse des Athleten?
 *
 * Beim Eintippen warnt das Panel ("FALSCHE KATEGORIE" mit Wechselvorschlag).
 * Danach nicht mehr: in der Liste stand nur die Zahl und ein gruener Punkt.
 * Ein Gewicht, das anders hereinkam -- ueber die Schnittstelle, eine spaeter
 * geaenderte Klasse oder einen Tippfehler, den jemand bestaetigt hat --
 * blieb damit unentdeckt.
 */
function ausserhalbDerKlasse(gewicht, athlet) {
  if (gewicht === '' || gewicht == null || !athlet) return false;
  const g = parseFloat(gewicht);
  if (isNaN(g) || g <= 0) return false;
  const min = athlet.div_min != null && athlet.div_min !== '' ? parseFloat(athlet.div_min) : null;
  const max = athlet.div_max != null && athlet.div_max !== '' ? parseFloat(athlet.div_max) : null;
  if (min !== null && g < min - 0.1) return true;
  if (max !== null && g > max + 0.05) return true;
  return false;
}

export default function EinwaageDashboard({ selectedTurnier, staffMode = false }) {
  const { t } = useTranslation();
  const [kaempfer, setKaempfer]         = useState([]);
  const [stats, setStats]               = useState(null);
  const [loading, setLoading]           = useState(false);
  const [suche, setSuche]               = useState('');
  const [filterStatus, setFilterStatus] = useState('alle');
  const [saving, setSaving]             = useState({});
  const [inline, setInline]             = useState({});
  const [msg, setMsg]                   = useState(null);
  const [syncResult, setSyncResult]     = useState(null);
  const [syncing, setSyncing]           = useState(false);
  const [regen, setRegen]               = useState(null);
  const [regening, setRegening]         = useState(false);

  // Wiegemodus
  const [mode, setMode]                 = useState('waage'); // 'waage' | 'tabelle'
  const [activeWkId, setActiveWkId]     = useState(null);
  const [waageWert, setWaageWert]       = useState('');
  const [waageFlash, setWaageFlash]     = useState(null); // 'ok' | 'over' | null
  const waageInputRef                   = useRef(null);
  const [weightClasses, setWeightClasses]     = useState([]);
  const [katWechsel, setKatWechsel]           = useState({}); // wkId → {loading, result}
  // Aufgabe 206: Kämpfe automatisch erzeugen, sobald eine Division fertig gewogen ist
  const [automatik, setAutomatik]             = useState(false);
  const [automatikSpeichert, setAutomatikSpeichert] = useState(false);


  const turnierId = selectedTurnier?.turnier_id;
  const token     = localStorage.getItem('token');

  // ── Laden ────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!turnierId) return;
    setLoading(true);
    try {
      const r = await fetch(`/api/einwaage/${turnierId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await r.json();
      if (d.success) {
        setKaempfer(d.data);
        setStats(d.stats);
        setAutomatik(!!d.automatik);
        const init = {};
        d.data.forEach(k => {
          init[k.wettkaempfer_id] = {
            gewicht:   k.gewicht_einwaage ?? '',
            status:    k.status || 'ausstehend',
            kommentar: k.kommentar || '',
          };
        });
        setInline(init);
        // Ersten ausstehenden Kämpfer vorauswählen
        setActiveWkId(prev => {
          if (prev) return prev;
          const erster = d.data.find(k => !k.status || k.status === 'ausstehend');
          return erster?.wettkaempfer_id ?? d.data[0]?.wettkaempfer_id ?? null;
        });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [turnierId, token]);

  useEffect(() => { load(); }, [load]);

  // Gewichtsklassen für Kategorie-Check laden
  useEffect(() => {
    if (!turnierId) return;
    fetch(`/api/einwaage/${turnierId}/weight-classes`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(d => { if (d.success) setWeightClasses(d.data); })
      .catch(() => {});
  }, [turnierId, token]);

  // ── Wiegemodus: Eingabefeld fokussieren wenn Athlet wechselt ─────
  useEffect(() => {
    if (mode !== 'waage' || !activeWkId) return;
    const gespeichert = inline[activeWkId]?.gewicht;
    setWaageWert(gespeichert !== undefined && gespeichert !== null ? String(gespeichert) : '');
    setWaageFlash(null);
    setTimeout(() => {
      waageInputRef.current?.focus();
      waageInputRef.current?.select();
    }, 50);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWkId, mode]);

  const flashMsg = (text, ok = true, ms = 3000) => {
    setMsg({ text, ok });
    setTimeout(() => setMsg(null), ms);
  };

  // Was die Automatik nach dem Speichern erzeugt hat (Backend: erzeugt[])
  const erzeugtText = (erzeugt) => (erzeugt?.length
    ? ` · Fertig gewogen: ${erzeugt.map(e => `${e.division_code} (${e.kaempfe} Kämpfe)`).join(', ')} erzeugt`
    : '');

  const automatikUmschalten = async (aktiv) => {
    setAutomatikSpeichert(true);
    try {
      const r = await fetch(`/api/einwaage/${turnierId}/automatik`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ aktiv }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.success) { flashMsg(d.error || 'Einstellung nicht gespeichert', false); return; }
      setAutomatik(!!d.aktiv);
      flashMsg((d.aktiv ? 'Automatik an' : 'Automatik aus') + erzeugtText(d.erzeugt), true, d.erzeugt?.length ? 8000 : 3000);
    } catch {
      flashMsg('Netzwerkfehler', false);
    } finally {
      setAutomatikSpeichert(false);
    }
  };

  const setField = (wkId, field, value) => {
    setInline(s => ({ ...s, [wkId]: { ...s[wkId], [field]: value } }));
  };

  // ── Speichern ─────────────────────────────────────────────────────
  const save = async (wkId, overrideRow) => {
    const row = overrideRow ?? inline[wkId];
    if (!row) return;
    setSaving(s => ({ ...s, [wkId]: true }));
    try {
      const r = await fetch(`/api/einwaage/${turnierId}/${wkId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          gewicht_einwaage: row.gewicht !== '' ? parseFloat(row.gewicht) : null,
          status:    row.status,
          kommentar: row.kommentar || null,
        })
      });
      const d = await r.json();
      if (r.ok && d.success) {
        flashMsg('Gespeichert ✓' + erzeugtText(d.erzeugt), true, d.erzeugt?.length ? 8000 : 3000);
        load();
      } else {
        flashMsg('Fehler beim Speichern', false);
      }
    } catch {
      flashMsg('Netzwerkfehler', false);
    } finally {
      setSaving(s => ({ ...s, [wkId]: false }));
    }
  };

  const quickStatus = async (wkId, status) => {
    setField(wkId, 'status', status);
    setSaving(s => ({ ...s, [wkId]: true }));
    const row = inline[wkId] ?? {};
    try {
      // 02.10.2026: Antwort wurde nie gelesen — ein Fehler blieb stumm
      const r = await fetch(`/api/einwaage/${turnierId}/${wkId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          gewicht_einwaage: row.gewicht !== '' ? parseFloat(row.gewicht) : null,
          status,
          kommentar: row.kommentar || null,
        })
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.success) flashMsg(d.error || 'Fehler beim Speichern', false);
      else if (d.erzeugt?.length) flashMsg('Gespeichert ✓' + erzeugtText(d.erzeugt), true, 8000);
      load();
    } catch {
      flashMsg('Netzwerkfehler', false);
    } finally {
      setSaving(s => ({ ...s, [wkId]: false }));
    }
  };

  // ── Wiegemodus: Enter → speichern + nächster ─────────────────────
  const naechsterAusstehend = (fromWkId) => {
    const idx = kaempfer.findIndex(k => k.wettkaempfer_id === fromWkId);
    // Erstmal vorwärts suchen
    for (let i = idx + 1; i < kaempfer.length; i++) {
      if (!inline[kaempfer[i].wettkaempfer_id]?.status ||
           inline[kaempfer[i].wettkaempfer_id]?.status === 'ausstehend') {
        return kaempfer[i].wettkaempfer_id;
      }
    }
    // Dann von vorne
    for (let i = 0; i < idx; i++) {
      if (!inline[kaempfer[i].wettkaempfer_id]?.status ||
           inline[kaempfer[i].wettkaempfer_id]?.status === 'ausstehend') {
        return kaempfer[i].wettkaempfer_id;
      }
    }
    return null;
  };

  const handleWaageEnter = async () => {
    if (!activeWkId || !waageWert) return;
    const parsed = parseWaagenInput(waageWert);
    if (!parsed) return;

    const k       = kaempfer.find(k => k.wettkaempfer_id === activeWkId);
    const gewicht = parseFloat(parsed);
    // gewicht_stamm kommt als DECIMAL-String ("0.00") und ist damit immer
    // truthy — ohne die > 0-Prüfung wurde das Limit 0 und jeder Kämpfer ohne
    // hinterlegtes Stammgewicht fiel automatisch durch.
    const stamm   = parseFloat(k?.gewicht_stamm);
    const limit   = Number.isFinite(stamm) && stamm > 0 ? stamm : null;
    const over    = limit !== null && gewicht > limit + 0.05;
    const wrongCat = findBetterCategory(parsed, k, weightClasses) !== null;
    const status  = (over || wrongCat) ? 'nicht_bestanden' : 'bestanden';

    // Flash-Feedback
    setWaageFlash(over ? 'over' : 'ok');
    setTimeout(() => setWaageFlash(null), 1200);

    const row = { gewicht: parsed, status, kommentar: inline[activeWkId]?.kommentar || '' };
    setInline(s => ({ ...s, [activeWkId]: row }));
    await save(activeWkId, row);

    // Nächsten ausstehenden auswählen
    const naechster = naechsterAusstehend(activeWkId);
    if (naechster) {
      setActiveWkId(naechster);
    }
  };

  const handleWaageKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleWaageEnter();
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const idx = kaempfer.findIndex(k => k.wettkaempfer_id === activeWkId);
      if (idx < kaempfer.length - 1) setActiveWkId(kaempfer[idx + 1].wettkaempfer_id);
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const idx = kaempfer.findIndex(k => k.wettkaempfer_id === activeWkId);
      if (idx > 0) setActiveWkId(kaempfer[idx - 1].wettkaempfer_id);
    }
  };

  // Panel-Klick → Eingabefeld zurückfokussieren
  const handlePanelClick = () => {
    waageInputRef.current?.focus();
  };

  const handleKategorieWechsel = async (wkId, new_division_code) => {
    setKatWechsel(s => ({ ...s, [wkId]: { loading: true } }));
    try {
      const r = await fetch(`/api/einwaage/${turnierId}/${wkId}/change-kategorie`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        // Alte Division mitschicken — sonst müsste der Server raten (02.10.2026)
        body: JSON.stringify({ new_division_code,
          old_division_code: kaempfer.find(k => k.wettkaempfer_id === wkId)?.division_code || undefined }),
      });
      const d = await r.json();
      if (d.success) {
        setKatWechsel(s => ({ ...s, [wkId]: { loading: false, result: d } }));
        // Der Wechsel raeumt das alte Bracket mit auf. Was dabei passiert ist,
        // muss das Personal erfahren: entweder sind Kaempfe entfallen und das
        // Bracket ist neu zu erzeugen, oder es liegt schon ein Ergebnis vor
        // und jemand muss von Hand nachsehen.
        if (d.hinweis) {
          flashMsg(`Kategorie gewechselt → ${d.new_division_name}. ${d.hinweis}${erzeugtText(d.erzeugt)}`, !d.bereits_gekaempft, 8000);
        } else {
          flashMsg(`Kategorie gewechselt → ${d.new_division_name}${erzeugtText(d.erzeugt)}`, true, d.erzeugt?.length ? 8000 : 3000);
        }
        load();
      } else {
        flashMsg(d.error || 'Fehler beim Wechsel', false);
        setKatWechsel(s => ({ ...s, [wkId]: { loading: false } }));
      }
    } catch {
      flashMsg('Netzwerkfehler', false);
      setKatWechsel(s => ({ ...s, [wkId]: { loading: false } }));
    }
  };

  const syncGewicht = async () => {
    setSyncing(true); setSyncResult(null);
    try {
      const r = await fetch(`/api/einwaage/${turnierId}/sync-gewicht`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }
      });
      const d = await r.json();
      if (d.success) { setSyncResult(d); flashMsg(`${d.updated} synchronisiert`); }
      else flashMsg('Fehler', false);
    } catch { flashMsg('Netzwerkfehler', false); }
    finally { setSyncing(false); }
  };

  const regenBrackets = async () => {
    setRegening(true); setRegen(null);
    try {
      const r = await fetch(`/api/brackets/turnier/${turnierId}/auto-generate`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }
      });
      const d = await r.json();
      if (d.success) { setRegen(d); flashMsg('Brackets neu generiert'); }
      else flashMsg('Fehler', false);
    } catch { flashMsg('Netzwerkfehler', false); }
    finally { setRegening(false); }
  };

  const gefiltert = kaempfer.filter(k => {
    if (filterStatus !== 'alle' && (k.status || 'ausstehend') !== filterStatus) return false;
    if (suche) {
      const q = suche.toLowerCase();
      if (!`${k.vorname} ${k.nachname}`.toLowerCase().includes(q) &&
          !k.verein_name?.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  if (!turnierId) return (
    <div className="ew-empty-turnier">
      <div className="ew-empty-icon">⚖</div>
      <p>{t('admin.common.select_tournament_first')}</p>
    </div>
  );

  const activeK        = kaempfer.find(k => k.wettkaempfer_id === activeWkId);
  const activeRow      = inline[activeWkId] ?? { gewicht: '', status: 'ausstehend', kommentar: '' };
  const diff           = activeK ? diffKg(activeK.gewicht_stamm, waageWert) : null;
  const diffNum        = diff !== null ? parseFloat(diff) : null;
  const betterCategory = waageWert ? findBetterCategory(waageWert, activeK, weightClasses) : null;

  return (
    <div className="ew-wrap">
      {msg && <div className={`ew-toast ${msg.ok ? 'ew-toast-ok' : 'ew-toast-err'}`}>{msg.text}</div>}

      {/* ── Header ── */}
      <div className="ew-header">
        <div className="ew-header-left">
          <h2 className="ew-title">⚖ {t('admin.einwaage.title')}</h2>
          {selectedTurnier?.name && <span className="ew-turnier-name">{selectedTurnier.name}</span>}
        </div>
        <div className="ew-header-right">
          <div className="ew-mode-toggle">
            <button className={`ew-mode-btn ${mode === 'waage' ? 'active' : ''}`}
              onClick={() => setMode('waage')}>⚡ Wiegemodus</button>
            <button className={`ew-mode-btn ${mode === 'tabelle' ? 'active' : ''}`}
              onClick={() => setMode('tabelle')}>📋 Übersicht</button>
          </div>
          <button className="ew-reload-btn" onClick={load} disabled={loading}>
            {loading ? '…' : '↻'}
          </button>
        </div>
      </div>

      {/* ── Automatik (Aufgabe 206) ── */}
      {turnierId && (
        <label className="ew-automatik" title="Sobald alle Gemeldeten einer Division gewogen sind (bestanden oder nicht erschienen), werden ihre Kämpfe erzeugt. Wer „nicht bestanden“ hat, hält die Division an.">
          <input type="checkbox" checked={automatik} disabled={automatikSpeichert}
            onChange={e => automatikUmschalten(e.target.checked)} />
          <span>Kämpfe automatisch erzeugen, sobald eine Division fertig gewogen ist</span>
        </label>
      )}

      {/* ── Stats ── */}
      {stats && (
        <div className="ew-stats-row">
          <div className="ew-stat"><span className="ew-stat-val">{stats.gesamt}</span><span className="ew-stat-lbl">Gesamt</span></div>
          <div className="ew-stat ew-stat-pending"><span className="ew-stat-val">{stats.ausstehend}</span><span className="ew-stat-lbl">Ausstehend</span></div>
          <div className="ew-stat ew-stat-pass"><span className="ew-stat-val">{stats.bestanden}</span><span className="ew-stat-lbl">Bestanden</span></div>
          <div className="ew-stat ew-stat-fail"><span className="ew-stat-val">{stats.nicht_bestanden}</span><span className="ew-stat-lbl">Nicht best.</span></div>
          <div className="ew-stat ew-stat-absent"><span className="ew-stat-val">{stats.nicht_erschienen}</span><span className="ew-stat-lbl">Abwesend</span></div>
        </div>
      )}

      {/* ════════════════════════════════════════════
          WIEGEMODUS
      ════════════════════════════════════════════ */}
      {mode === 'waage' && (
        <div className="ew-waage-layout">

          {/* Linke Seite: Aktiver Athlet */}
          <div
            className={`ew-active-panel${waageFlash ? ` ew-flash-${waageFlash}` : ''}`}
            onClick={handlePanelClick}
          >
            {activeK ? (
              <>
                <div className="ew-active-badge">
                  <span className={`ew-active-status-dot ${activeRow.status}`} />
                  {STATUS_LABEL[activeRow.status]?.label}
                </div>

                <div className="ew-active-name">
                  {activeK.vorname} <strong>{activeK.nachname}</strong>
                </div>
                <div className="ew-active-meta">
                  {activeK.verein_name}
                  {(activeK.division_codes || activeK.division_code || '')
                    .split(',').filter(Boolean)
                    .map(code => (
                      <span key={code} className="ew-active-div">{code}</span>
                    ))}
                </div>

                {/* Gewichtslimit */}
                {activeK.gewicht_stamm != null && (
                  <div className="ew-active-limit">
                    Angemeldet: <strong>{parseFloat(activeK.gewicht_stamm).toFixed(1)} kg</strong>
                  </div>
                )}

                {/* Waagen-Eingabefeld */}
                <div className="ew-waage-input-wrap">
                  <input
                    ref={waageInputRef}
                    type="text"
                    inputMode="decimal"
                    className={`ew-waage-input${diffNum !== null ? diffNum > 0.05 ? ' ew-input-over' : ' ew-input-ok' : ''}`}
                    value={waageWert}
                    onChange={e => setWaageWert(e.target.value)}
                    onKeyDown={handleWaageKeyDown}
                    placeholder="0.0"
                    autoComplete="new-password"
                  />
                  <span className="ew-waage-unit">kg</span>
                </div>

                {/* Differenz-Anzeige + Kategorie-Check */}
                {diffNum !== null && waageWert && (
                  <>
                    <div className={`ew-diff-display ${betterCategory ? 'wrong-cat' : diffNum > 0.05 ? 'over' : diffNum < -0.05 ? 'under' : 'ok'}`}>
                      {diffNum > 0 ? '+' : ''}{diff} kg
                      {betterCategory
                        ? <span className="ew-diff-label"> FALSCHE KATEGORIE</span>
                        : diffNum > 0.05
                          ? <span className="ew-diff-label"> ÜBERGEWICHT</span>
                          : <span className="ew-diff-label"> ✓</span>
                      }
                    </div>
                    {betterCategory && !katWechsel[activeWkId]?.result && (
                      <div className="ew-cat-suggestion">
                        <div className="ew-cat-suggestion-top">
                          <span className="ew-cat-suggestion-label">Sollte in:</span>
                          <span className="ew-cat-suggestion-name">{betterCategory.division_name}</span>
                          <span className="ew-cat-suggestion-code">{betterCategory.division_code}</span>
                        </div>
                        <button
                          className="ew-cat-switch-btn"
                          onClick={() => handleKategorieWechsel(activeWkId, betterCategory.division_code)}
                          disabled={katWechsel[activeWkId]?.loading}
                        >
                          {katWechsel[activeWkId]?.loading ? '⏳ Wechsle…' : '⇄ Kategorie wechseln'}
                        </button>
                      </div>
                    )}
                    {katWechsel[activeWkId]?.result && (
                      <div className="ew-cat-changed">
                        <span className="ew-cat-changed-old">{katWechsel[activeWkId].result.old_division_code}</span>
                        <span className="ew-cat-changed-arrow">→</span>
                        <span className="ew-cat-changed-new">{katWechsel[activeWkId].result.new_division_code}</span>
                        <span className="ew-cat-changed-ok">✓ Wechsel erfolgreich</span>
                      </div>
                    )}
                  </>
                )}

                {/* Enter-Button (alternativ zur Waage) */}
                <button
                  className="ew-confirm-btn"
                  onClick={handleWaageEnter}
                  disabled={!waageWert || saving[activeWkId]}
                >
                  {saving[activeWkId] ? '…' : '↵ Übernehmen'}
                </button>

                {/* Status-Buttons */}
                <div className="ew-waage-quick-btns">
                  <button
                    className={`ew-wq-btn ew-wq-pass ${activeRow.status === 'bestanden' ? 'active' : ''}`}
                    onClick={() => quickStatus(activeWkId, 'bestanden')}
                  >✓ Bestanden</button>
                  <button
                    className={`ew-wq-btn ew-wq-fail ${activeRow.status === 'nicht_bestanden' ? 'active' : ''}`}
                    onClick={() => quickStatus(activeWkId, 'nicht_bestanden')}
                  >✗ Nicht best.</button>
                  <button
                    className={`ew-wq-btn ew-wq-absent ${activeRow.status === 'nicht_erschienen' ? 'active' : ''}`}
                    onClick={() => quickStatus(activeWkId, 'nicht_erschienen')}
                  >— Abwesend</button>
                </div>

                {/* Kommentar */}
                <input
                  type="text"
                  className="ew-waage-kommentar"
                  placeholder="Notiz (optional)"
                  value={activeRow.kommentar}
                  onChange={e => setField(activeWkId, 'kommentar', e.target.value)}
                  maxLength={100}
                  onFocus={e => e.stopPropagation()}
                />

                <div className="ew-waage-hint">
                  Waage auf Modus <strong>STAb</strong> einstellen — Gewicht wird automatisch übernommen
                </div>
              </>
            ) : (
              <div className="ew-no-active">Kein Athlet ausgewählt</div>
            )}
          </div>

          {/* Rechte Seite: Athleten-Liste */}
          <div className="ew-waage-list-panel">
            <div className="ew-waage-list-header">
              <input
                className="ew-waage-search"
                placeholder="Suchen…"
                value={suche}
                onChange={e => setSuche(e.target.value)}
              />
              <div className="ew-waage-filter-btns">
                {['alle','ausstehend','bestanden','nicht_bestanden','nicht_erschienen'].map(s => (
                  <button key={s}
                    className={`ew-wf-btn ${filterStatus === s ? 'active' : ''}`}
                    onClick={() => setFilterStatus(s)}
                    title={s === 'alle' ? 'Alle' : STATUS_LABEL[s]?.label}
                  >
                    {s === 'alle' ? 'Alle' : s === 'ausstehend' ? '⏳' : s === 'bestanden' ? '✓' : s === 'nicht_bestanden' ? '✗' : '—'}
                  </button>
                ))}
              </div>
              <span className="ew-waage-list-count">{gefiltert.length}</span>
            </div>

            <div className="ew-waage-list">
              {gefiltert.map((k, idx) => {
                const wkId    = k.wettkaempfer_id;
                const row     = inline[wkId] ?? { status: 'ausstehend' };
                const isActive = wkId === activeWkId;
                return (
                  <div
                    key={wkId}
                    className={`ew-list-row ${isActive ? 'active' : ''} ew-row-${row.status}`}
                    onClick={() => setActiveWkId(wkId)}
                  >
                    <span className="ew-list-nr">{idx + 1}.</span>
                    <div className="ew-list-info">
                      <span className="ew-list-name">{k.vorname} {k.nachname}</span>
                      <span className="ew-list-verein">{k.verein_name}</span>
                      {(k.original_division_code || katWechsel[wkId]?.result) && (
                        <span className="ew-list-kat-change">
                          <span className="ew-list-kat-old">
                            {k.original_division_code || katWechsel[wkId]?.result?.old_division_code}
                          </span>
                          <span className="ew-list-kat-arrow">→</span>
                          <span className="ew-list-kat-new">
                            {k.neue_division_code || katWechsel[wkId]?.result?.new_division_code}
                          </span>
                        </span>
                      )}
                    </div>
                    <div className="ew-list-right">
                      {row.gewicht && row.gewicht !== '' && (
                        <span className={`ew-list-gewicht${ausserhalbDerKlasse(row.gewicht, k) ? ' ausser-klasse' : ''}`}
                          title={ausserhalbDerKlasse(row.gewicht, k)
                            ? `Passt nicht in ${k.division_code} (${parseFloat(k.div_min || 0).toFixed(1)}–${parseFloat(k.div_max || 0).toFixed(1)} kg)`
                            : undefined}>
                          {parseFloat(row.gewicht).toFixed(1)}
                          {ausserhalbDerKlasse(row.gewicht, k) && <span className="ew-list-warnung">!</span>}
                        </span>
                      )}
                      <span className={`ew-list-status-dot ${row.status}`} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════
          TABELLEN-ÜBERSICHT
      ════════════════════════════════════════════ */}
      {mode === 'tabelle' && (
        <>
          <div className="ew-filter-row">
            <input className="ew-search" placeholder={t('admin.einwaage.search')}
              value={suche} onChange={e => setSuche(e.target.value)} />
            <div className="ew-status-filter">
              {['alle','ausstehend','bestanden','nicht_bestanden','nicht_erschienen'].map(s => (
                <button key={s}
                  className={`ew-filter-btn ${filterStatus === s ? 'active' : ''}`}
                  onClick={() => setFilterStatus(s)}>
                  {s === 'alle' ? 'Alle' : STATUS_LABEL[s]?.label}
                </button>
              ))}
            </div>
            <span className="ew-count">{gefiltert.length} Kämpfer</span>
          </div>

          {loading ? (
            <div className="ew-loading">{t('admin.einwaage.loading')}</div>
          ) : gefiltert.length === 0 ? (
            <div className="ew-empty">{t('admin.common.no_results')}</div>
          ) : (
            <div className="ew-table-wrap">
              <table className="ew-table">
                <thead>
                  <tr>
                    <th>Kämpfer</th><th>Verein</th><th>Klasse / Wechsel</th>
                    <th>Angemeldet</th><th>Einwaage</th><th>Diff</th>
                    <th>Status</th><th>Notiz</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {gefiltert.map(k => {
                    const wkId = k.wettkaempfer_id;
                    const row  = inline[wkId] ?? { gewicht: '', status: 'ausstehend', kommentar: '' };
                    const diff = diffKg(k.gewicht_stamm, row.gewicht);
                    const diffNum = diff !== null ? parseFloat(diff) : null;
                    const statusInfo = STATUS_LABEL[row.status] ?? STATUS_LABEL.ausstehend;
                    return (
                      <tr key={wkId} className={`ew-row ew-row-${row.status}`}>
                        <td className="ew-cell-name">
                          <span className="ew-fighter-name">{k.vorname} {k.nachname}</span>
                          {k.gurtfarbe && <span className="ew-gurt">{k.gurtfarbe}</span>}
                        </td>
                        <td className="ew-cell-club">{k.verein_name}</td>
                        <td className="ew-cell-div">
                          {k.original_division_code ? (
                            <div className="ew-div-changed">
                              <span className="ew-div-badge ew-div-old">{k.original_division_code}</span>
                              <span className="ew-div-arrow">→</span>
                              <span className="ew-div-badge ew-div-new">{k.neue_division_code || k.division_code}</span>
                              <span className="ew-div-changed-ok" title="Kategorie gewechselt">✓</span>
                            </div>
                          ) : (
                            // Alle Divisionen zeigen — gewogen wird einmal, das
                            // Ergebnis gilt für jede Kategorie des Kämpfers.
                            (k.division_codes || k.division_code || '')
                              .split(',').filter(Boolean)
                              .map(code => (
                                <span key={code} className="ew-div-badge">{code}</span>
                              ))
                          )}
                        </td>
                        <td className="ew-cell-weight">
                          {k.gewicht_stamm != null ? `${parseFloat(k.gewicht_stamm).toFixed(1)} kg` : '—'}
                        </td>
                        <td className="ew-cell-einwaage">
                          <input type="number" step="0.1" min="20" max="200"
                            className="ew-weight-input" value={row.gewicht}
                            onChange={e => setField(wkId, 'gewicht', e.target.value)}
                            placeholder="kg" />
                        </td>
                        <td className={`ew-cell-diff ${diffNum === null ? '' : diffNum > 0 ? 'ew-diff-over' : diffNum < 0 ? 'ew-diff-under' : 'ew-diff-ok'}`}>
                          {diffNum !== null ? `${diffNum > 0 ? '+' : ''}${diff} kg` : '—'}
                        </td>
                        <td className="ew-cell-status">
                          <div className="ew-quick-btns">
                            <button className={`ew-quick-btn ew-btn-pass ${row.status === 'bestanden' ? 'active' : ''}`}
                              onClick={() => quickStatus(wkId, 'bestanden')}>✓</button>
                            <button className={`ew-quick-btn ew-btn-fail ${row.status === 'nicht_bestanden' ? 'active' : ''}`}
                              onClick={() => quickStatus(wkId, 'nicht_bestanden')}>✗</button>
                            <button className={`ew-quick-btn ew-btn-absent ${row.status === 'nicht_erschienen' ? 'active' : ''}`}
                              onClick={() => quickStatus(wkId, 'nicht_erschienen')}>—</button>
                          </div>
                          <span className={`ew-status-badge ${statusInfo.css}`}>{statusInfo.label}</span>
                        </td>
                        <td className="ew-cell-comment">
                          <input type="text" className="ew-comment-input" value={row.kommentar}
                            onChange={e => setField(wkId, 'kommentar', e.target.value)}
                            placeholder="Notiz" maxLength={100} />
                        </td>
                        <td className="ew-cell-save">
                          <button className="ew-save-btn" onClick={() => save(wkId)}
                            disabled={saving[wkId]}>{saving[wkId] ? '…' : '💾'}</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── Bracket-Sync (nur Admin) ── */}
      {!staffMode && <div className="ew-sync-section">
        <div className="ew-sync-header">
          <h3 className="ew-sync-title">⚡ {t('admin.einwaage.sync_title')}</h3>
          <p className="ew-sync-desc">{t('admin.einwaage.sync_desc')}</p>
        </div>
        <div className="ew-sync-actions">
          <button className="ew-sync-btn ew-sync-btn-primary" onClick={syncGewicht}
            disabled={syncing || stats?.bestanden === 0}>
            {syncing ? '⏳ Läuft…' : `⚖ ${t('admin.einwaage.sync_btn')}`}
          </button>
          <button className="ew-sync-btn ew-sync-btn-secondary" onClick={regenBrackets}
            disabled={regening}>
            {regening ? '⏳ Läuft…' : `🔄 ${t('admin.einwaage.regen_btn')}`}
          </button>
        </div>
        {syncResult && (
          <div className="ew-sync-result">
            <div className="ew-sync-result-header">
              <span className="ew-sync-result-title">
                {t('admin.einwaage.sync_result_title', { updated: syncResult.updated, changed: syncResult.geaendert })}
              </span>
            </div>
            {syncResult.aenderungen?.filter(a => a.geaendert).length > 0 && (
              <table className="ew-sync-table">
                <thead><tr><th>Kämpfer</th><th>Verein</th><th>Klasse</th><th>Alt</th><th>Neu</th><th>Δ</th></tr></thead>
                <tbody>
                  {syncResult.aenderungen.filter(a => a.geaendert).map(a => (
                    <tr key={a.wettkaempfer_id}>
                      <td>{a.name}</td><td>{a.verein}</td><td>{a.division_code || '—'}</td>
                      <td>{a.gewicht_alt.toFixed(1)}</td><td>{a.gewicht_neu.toFixed(1)}</td>
                      <td className={a.delta > 1 ? 'ew-delta-high' : 'ew-delta-low'}>
                        {a.delta > 0 ? `+${a.delta.toFixed(2)}` : `${a.delta.toFixed(2)}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {syncResult.aenderungen?.filter(a => a.geaendert).length === 0 && (
              <p className="ew-sync-no-change">{t('admin.einwaage.no_changes')}</p>
            )}
          </div>
        )}
        {regen && <div className="ew-regen-result"><span>✓ {regen.message || 'Brackets neu generiert.'}</span></div>}
      </div>}
    </div>
  );
}
